import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import {
  Plus,
  Edit,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter
} from 'lucide-react';
import toast from 'react-hot-toast';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import gracePeriodConfigurationService from '../../api/gracePeriodConfigurationService';
import { getRoleCodeFromId } from '../../utils/roleMapper';

const PAGE_SIZE = 10;
const ALLOWED_ROLES = new Set(['ADMIN', 'SUPER_ADMIN']);

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' }
];

const emptyForm = () => ({
  gracePeriodHours: '',
  gracePeriodMinutes: '',
  description: '',
  status: true
});

const formatDuration = (hours, minutes) => {
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

/**
 * Grace Period Configuration — ADMIN / SUPER_ADMIN only.
 */
const GracePeriodConfigurationManagement = () => {
  const roleId = Number(sessionStorage.getItem('roleId') || sessionStorage.getItem('role_id') || 0);
  const roleCode = (getRoleCodeFromId(roleId) || sessionStorage.getItem('roleCode') || '').toUpperCase();
  const canAccess = ALLOWED_ROLES.has(roleCode);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter]);

  const loadPage = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const statusParam =
        statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;
      const pageData = await gracePeriodConfigurationService.search({
        search: debouncedSearch,
        status: statusParam,
        page: currentPage,
        size: PAGE_SIZE
      });
      const content = Array.isArray(pageData?.content) ? pageData.content : [];
      setRows(content);
      setTotalPages(pageData?.totalPages ?? 0);
      setTotalElements(pageData?.totalElements ?? content.length);
    } catch (error) {
      console.error('Error loading grace period configurations:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load grace period configurations');
      setRows([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [canAccess, debouncedSearch, statusFilter, currentPage]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  if (!canAccess) {
    return <Navigate to="/home" replace />;
  }

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      gracePeriodHours: row.gracePeriodHours != null ? String(row.gracePeriodHours) : '',
      gracePeriodMinutes: row.gracePeriodMinutes != null ? String(row.gracePeriodMinutes) : '',
      description: row.description || '',
      status: row.status !== false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const hours = Number(formData.gracePeriodHours);
    const minutes = Number(formData.gracePeriodMinutes);
    if (!Number.isInteger(hours) || hours < 0) {
      toast.error('Hours must be a whole number greater than or equal to 0');
      return;
    }
    if (!Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
      toast.error('Minutes must be a whole number between 0 and 59');
      return;
    }
    if (hours === 0 && minutes === 0) {
      toast.error('Hours and minutes cannot both be zero');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        gracePeriodHours: hours,
        gracePeriodMinutes: minutes,
        description: formData.description?.trim() || '',
        status: formData.status !== false
      };

      if (editing?.id) {
        await gracePeriodConfigurationService.update(editing.id, payload);
        toast.success('Grace period configuration updated successfully');
      } else {
        await gracePeriodConfigurationService.create(payload);
        toast.success('Grace period configuration created successfully');
      }
      setShowModal(false);
      setEditing(null);
      await loadPage();
    } catch (error) {
      console.error('Error saving grace period configuration:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save grace period configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row) => {
    const next = !(row.status !== false);
    try {
      await gracePeriodConfigurationService.updateStatus(row.id, next);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: next } : r)));
      toast.success(`Grace period configuration ${next ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling grace period configuration status:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update status');
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Clock className="h-7 w-7 text-primary" />
            Grace Period Configuration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage grace period settings (hours, minutes, description, status).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPage} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Configuration
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="text-sm font-medium text-foreground mb-1 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="w-full md:w-48">
            <label className="text-sm font-medium text-foreground mb-1 block">Status</label>
            <StatusFilterDropdown
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Filter by Status"
              onChange={setStatusFilter}
              icon={Filter}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left p-3 text-sm font-semibold">S.No</th>
                  <th className="text-left p-3 text-sm font-semibold">Grace Period</th>
                  <th className="text-left p-3 text-sm font-semibold">Description</th>
                  <th className="text-left p-3 text-sm font-semibold">Status</th>
                  <th className="text-left p-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Loading...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      No grace period configurations found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => (
                    <tr key={row.id} className="border-b border-border hover:bg-muted/30">
                      <td className="p-3 text-sm">{currentPage * PAGE_SIZE + index + 1}</td>
                      <td className="p-3 text-sm font-medium">
                        {formatDuration(row.gracePeriodHours, row.gracePeriodMinutes)}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground max-w-xs truncate" title={row.description || ''}>
                        {row.description || '—'}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={row.status !== false}
                            onCheckedChange={() => handleToggleStatus(row)}
                          />
                          <Badge className={row.status !== false
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : 'bg-gray-100 text-gray-700 border-gray-300'}>
                            {row.status !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {totalElements} total · Page {totalPages === 0 ? 0 : currentPage + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 0 || loading}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= totalPages - 1 || totalPages === 0 || loading}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => !saving && setShowModal(false)}
        title={editing ? 'Edit Grace Period Configuration' : 'Add Grace Period Configuration'}
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Grace Period</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Hours *</label>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={formData.gracePeriodHours}
                  onChange={(e) => setFormData((f) => ({ ...f, gracePeriodHours: e.target.value }))}
                  placeholder="e.g. 24"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Minutes *</label>
                <Input
                  type="number"
                  min={0}
                  max={59}
                  step={1}
                  value={formData.gracePeriodMinutes}
                  onChange={(e) => setFormData((f) => ({ ...f, gracePeriodMinutes: e.target.value }))}
                  placeholder="e.g. 0"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
              rows={3}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.status !== false}
              onCheckedChange={(checked) => setFormData((f) => ({ ...f, status: checked }))}
            />
            <span className="text-sm">Active</span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GracePeriodConfigurationManagement;
